// src/annihilation/PlayerController.js
// ============================================================
//  ANNIHILATION — the player controller (Batch 5).
//  Source of truth: ANNIHILATION_SYSTEMS_SPEC (v2) Parts 2–3
//  (Lyt economy, master damage formula), beings.js (Lyt attack
//  kits), skilltrees data (node effects).
//
//  ONE hook — usePlayerController — is the field-combat brain
//  for EVERY zone, dungeon, and yard from here to launch. Zones
//  mount it and render its state; they never re-implement
//  movement, the Lyt pool, the combo, or iframes. (Overseer
//  pattern — the same law that made treeEngine the one gate.)
//
//  LAWS HONORED:
//  · Damage resolves ONLY through combat.js resolveDamage /
//    resolveDotTick. No inline math, no percentages of Lyf.
//  · Lyt swings pay through combat.js payLytSwing. Running dry
//    is a ~2-second breather (stats.LYT_FULL_REGEN_SECONDS).
//  · Levels derive from xp; stats derive from statsAtLevel.
//  · Skill nodes are read from the spent map — the controller
//    DERIVES its behavior from owned nodes every render, so a
//    point spent at Sigfer's changes the very next swing.
//
//  Node effects live in BEING_COMBAT_MODS below — Spectral
//  Tiger's Lyt cluster ships now (the template); the other
//  three beings run base kits until Batches 12–14 add their
//  trees (one entry each here, nothing else changes).
//
//  ⚑⚑ BATCH GATE-1 (caP'n, Jul 25 2026) ⚑⚑
//  Two Doctrine changes land in this file:
//
//  1. GATE B — ACTIVATION. Every castable ability now carries
//     the same `aenergyReq` shape the skill trees use, checked
//     against the CURRENT pool at the moment you press the key.
//     ONE HELPER, TWO CALLERS: `aenergyShortfall` lives in
//     treeEngine; the Trainer passes it the MAX pool (Gate A —
//     what you can OWN) and this file passes it the live one
//     (Gate B — what you can USE). Your ceiling decides what you
//     own; your moment decides what you can fire.
//     TIER GATES ARE GONE from `slotUnlocked` — a slot lights
//     when you can HOLD it, never when you are old enough.
//
//  2. DECREE replaces crit. `BASE_CRIT_CHANCE` is deleted from
//     combat.js; hits now report earned openings instead of dice
//     rolls. Two of the four triggers are live today with zero
//     zone changes (combo-end, your own Primera window); the
//     other two read OPTIONAL enemy fields (`facing` for behind,
//     `staggered`/`stunUntil`/`marked` for set-ups), so zones
//     opt in by exposing a field and nothing breaks until they do.
//     `onHit` still carries `crit` as a compat alias so the four
//     zone FX files keep flaring gold — see combat.js.
// ============================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBeing } from "./beings";
import {
  levelForXp,
  statsAtLevel,
  aenergyPool,
  tierForLevel,
  SPENDABLE_AENERGY,
  AENERGY_LABEL,
  LYT_FULL_REGEN_SECONDS,
  CHAO_REGEN_PER_SEC,
} from "./stats";
import {
  resolveDamage,
  resolveDotTick,
  payLytSwing,
  STATUS_EFFECTS,
  DECREE_TRIGGERS,
} from "./combat";
import { aenergyShortfall, describeAenergyReq } from "./treeEngine";
import { normalizeTreeState, SLOT_UNLOCK_NODES } from "./trees";

// ------------------------------------------------------------
// TUNING — feel numbers, freely adjustable. Shapes are law;
// these are dials.
//
// ⚑ Jul 21 SWARM-RELIEF pass (caP'n: "I get swarmed and only make
// it 5 feet"). The dodge is the reposition tool, so it's the lever:
// dash a tile farther, longer i-frames to slip THROUGH a crowd, and
// a shorter cooldown so you can chain out of a pile-up. Faster Lyt
// regen (the other half of the relief) lives in stats.js
// (LYT_FULL_REGEN_SECONDS 2.5→1.8) and flows in via the import.
// Every value here is a dial — revert any single number to taste.
// ------------------------------------------------------------
export const FEEL = {
  stepMs: 110, //            legacy grid cadence (speed derives from it)
  moveTickMs: 50, //         ⚑ v4 analog tick — position integrates each tick
  moveSpeedTiles: 1000 / 110, // tiles/sec — exactly the old feel, now continuous
  playerRadius: 0.42, //     v4 — collision body for slide/blocking
  comboWindowMs: 650, //     time to land the next swing of a combo
  swingRecoverMs: 220, //    can't move mid-swing
  swingIframeMs: 300, //     iframes on the phase-swipe follow-through
  dodgeTiles: 3, //          ⚑ was 2 — dash farther to clear a swarm
  dodgeMs: 190, //           dash duration (nudged for the longer dash)
  dodgeIframeMs: 520, //     ⚑ was 400 — stay untouchable through the crowd
  dodgeCooldownMs: 650, //   ⚑ was 800 — chain dodges out of a pile-up
  lytRegenDelayMs: 500, //   ⚑ was 600 — pool starts refilling sooner
  attackArcTiles: 1.6, //    melee reach in front of the player
};

// ------------------------------------------------------------
// THE CAST PASS (Batch FX-1) — slots 2–5 become live verbs.
// Spectral Tiger ships first (the template, same as the combat
// mods above); the other three lineages land with Batches 12–14
// as data adds. Costs/cooldowns are DIALS inside the spec's own
// ranges (SKILL_SLOTS cooldownSec: primera [2,4], impera [6,10],
// uhlti [20,40]). Chao costs derive from the spec's cadence note
// (Beb regen 2/sec → "small Primera every 3 sec, Impera after
// ~7 sec build-up" → 6 and 14).
//
// ⚑ DEV_PREVIEW_ALL_SLOTS — review-session lever. Flip to true
// and every slot casts regardless of tier/node gates, so a Beb
// character can preview the Lord's arsenal. SHIP FALSE. The
// gates below are the law (they mirror SkillBar's derivation
// exactly); this flag exists so a visuals review never has to
// bend the law in data.
// ------------------------------------------------------------
export const DEV_PREVIEW_ALL_SLOTS = false;

export const CAST = {
  // COST is SPENT out of a live pool. AENERGYREQ is a THRESHOLD
  // you must be able to hold — checked against the MAX pool to
  // light the slot (Gate A) and against the CURRENT pool to fire
  // it (Gate B). Same field, same helper, two different numbers.
  //
  // THE FLUIDITY LAW: no ability is REQUIRED to cost any
  // particular Aenergy. Nothing below is hardcoded per slot —
  // these are this being's numbers, on this ability, today.
  //
  // ⚑ The aenergyReq numbers are tuned to the natural growth
  // curves in stats.js so that Impera lands around L28-L30 and
  // U'hlti around L60 WITHOUT anything checking a tier. Xulan
  // (the largest Chao pool) reaches Impera a level or two early;
  // that is the STEERING LAW working, not a leak. DIALS, all.
  primera: {
    cost: { chao: 6 },
    aenergyReq: null, //     the first verb after Transcendence — free to hold
    cdMs: 3000,
    armedMs: 6000,
  },
  impera: {
    cost: { chao: 14 },
    aenergyReq: { chao: 125 }, // was: Champion tier
    cdMs: 8000,
    claws: 3, //             beings.js spectral_claws.claws
    bonusPerClaw: 12, //     spec Part 3's own worked example ("+12 per claw")
    missingAnimaPct: 0.15, // pool fraction — NOT a stat magnitude, stays a %
    rangeTiles: 9, //        seek range ("on screen")
  },
  uhlti: {
    // Doctrine Part 3: a U'hlti leans on Chao AND Anima. Paying
    // in life is the point — it can never take your last drop
    // (the pay step floors Anima at 1).
    cost: { chao: 35, anima: 15 },
    aenergyReq: { chao: 245, anima: 400 }, // was: Lord tier
    cdMs: 30000,
    bonus: 20, //            flat shadow-damage bonus (dial)
    // shadow damage bypasses Lyf here (DoT-law reading of
    // "shadow damage"). Flagged §4.25 — one boolean if re-ruled.
    bypassLyf: true,
  },
  transformation: {
    cost: { chao: 20 },
    aenergyReq: null, //     node-gated, not Aenergy-gated
    cdMs: 30000,
    durationMs: 8000, //     beings.js: 8s
    dmgMult: 0.7, //         "phantom swipes - lower damage"
    speedMult: 2, //         "movement speed doubles" (spatial - stays a %)
  },
};

// ⚑ GATE-1: the tier-gate helper that used to live here is
// DELETED. Slots 2-4 are gated by CAST[slot].aenergyReq against
// the MAX pool; slots 5-7 by node ownership. Nothing in this
// file asks how old you are.

/** Human-readable "why is this slot dark" for HUD tooltips. */
export const slotRequirementLabel = (slotId) =>
  describeAenergyReq(CAST[slotId]?.aenergyReq);

// ------------------------------------------------------------
// NODE-DRIVEN COMBAT MODS — beingId → derive(spent) → mods.
// Each entry reads ONLY internal node ids (vyren pattern).
// Batches 12–14: add one deriver per new being.
// ------------------------------------------------------------
const BEING_COMBAT_MODS = {
  spectral_tiger: (spent) => ({
    // st_l1 Keen Claws — first swipe +5 flat
    firstSwingBonus: spent.st_l1 ? 5 : 0,
    // st_l2 Phase Momentum — swing after a dodge costs 0 Lyt
    freeSwingAfterDodge: Boolean(spent.st_l2),
    // st_l4 Third Strike — combo becomes 3 hits, and the end of
    // a full string Decrees HARDER. (Nodes make Decrees bigger;
    // none of them make a Decree likelier. There is no
    // likelihood.) DIAL: comboEndDecreeBonus.
    extraComboHit: Boolean(spent.st_l4),
    comboEndDecreeBonus: spent.st_l4 ? 3 : 0,
    // st_l5a Bleed Specialist — swings apply Bleed (stacks to 5)
    applyBleed: Boolean(spent.st_l5a),
    // st_l5b Phase Specialist — follow-through iframes doubled
    iframeMultiplier: spent.st_l5b ? 2 : 1,
    // st_l6 Heart-Seeker — bleeding targets take +50% from Lyt
    // ⚑ SWEEP DEBT (§4.35): this is a stat-magnitude percentage
    // and the Small Number Law says it should be flat. Left as-is
    // deliberately — it belongs to the Tomes/cut sweep, not to
    // GATE-1, and only crit and Scaled Fury were pulled forward.
    // One number when that session runs.
    bleedAmp: spent.st_l6 ? 1.5 : 1,
    // st_l7 Ghost-Walker — iframes across the ENTIRE combo
    iframesWholeCombo: Boolean(spent.st_l7),
  }),
  chaos_dragon: () => ({}), // Batch 12 (chaos_stack mechanic lands with its tree)
  z4z: () => ({}), //          Batch 13
  xulan: () => ({}), //        Batch 14
};

const NO_MODS = {
  firstSwingBonus: 0,
  freeSwingAfterDodge: false,
  extraComboHit: false,
  comboEndDecreeBonus: 0,
  applyBleed: false,
  iframeMultiplier: 1,
  bleedAmp: 1,
  iframesWholeCombo: false,
};

// ------------------------------------------------------------
// TARGET SHAPE (the contract every zone's enemies satisfy):
//   { id, pos: {x,y}, anima, lyf, bleedStacks?, alive }
// The controller never mutates targets — it reports hits via
// events and the zone owns its enemy state (one owner per state).
// ------------------------------------------------------------

const DIRS = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  w: { x: 0, y: -1 },
  s: { x: 0, y: 1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 },
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * @param {object} cfg
 * @param {object} cfg.character   annihilation___character (beingId, xp, skillTrees)
 * @param {{x:number,y:number}} cfg.startPos   tile coords
 * @param {{minX,maxX,minY,maxY}} cfg.bounds   walkable rectangle (tiles)
 * @param {(pos)=>boolean} [cfg.isBlocked]     optional obstacle test
 * @param {()=>Array} cfg.getTargets           live enemy list (zone-owned)
 * @param {(hit)=>void} cfg.onHit              { targetId, damage, crit, bleedApplied }
 * @param {(evt)=>void} [cfg.onEvent]          feel events: swing/dodge/dry/hurt
 */
export function usePlayerController({
  character,
  startPos,
  bounds,
  isBlocked,
  getTargets,
  onHit,
  onEvent,
}) {
  const being = getBeing(character?.beingId);
  const { level } = levelForXp(character?.xp || 0);
  const block = useMemo(
    () => statsAtLevel(character?.beingId, level),
    [character?.beingId, level]
  );

  const spent = normalizeTreeState(character?.skillTrees).spent;
  const mods = useMemo(() => {
    const derive = BEING_COMBAT_MODS[character?.beingId];
    return { ...NO_MODS, ...(derive ? derive(spent) : {}) };
  }, [character?.beingId, spent]);

  const comboLength = (being?.lytAttack?.hits || 1) + (mods.extraComboHit ? 1 : 0);

  // ---- live state ------------------------------------------
  const [pos, setPos] = useState(startPos);
  const [facing, setFacing] = useState({ x: 0, y: 1 });
  const [anima, setAnima] = useState(block?.anima ?? 1);
  const [lyt, setLyt] = useState(block?.lytCap ?? 1);
  const [chao, setChao] = useState(block?.chao ?? 0); // FX-1: the cast pool
  const [comboStep, setComboStep] = useState(0); // 0 = idle
  const [dodging, setDodging] = useState(false);
  // FX-1 cast state — mirrors the lastSwing pattern exactly: a
  // transient per-cast record, keyed by id so every cast
  // re-triggers the 3D posture + world FX.
  const [lastCast, setLastCast] = useState(null); // { id, abilityId, slotId, at, origin, facing, targets, reformAt? }
  const [pawsArmed, setPawsArmed] = useState(false); // Spectral Paws buff live
  const [ghostForm, setGhostForm] = useState(false); // Ghost Form live
  const [nineSpent, setNineSpent] = useState(false); // Nine Lives used this mount
  // The visual makeover (July 19): a transient per-swing record.
  // Zones pass this straight to AnnihilationPlayer so the body
  // itself strikes — keyed by id, so every swing re-triggers.
  const [lastSwing, setLastSwing] = useState(null); // { id, step, at }
  const swingSeq = useRef(0);

  const keysRef = useRef({});
  const posRef = useRef(startPos);
  const facingRef = useRef({ x: 0, y: 1 });
  const lytRef = useRef(block?.lytCap ?? 1);
  const invulnUntil = useRef(0);
  const swingLockUntil = useRef(0);
  const dodgeReadyAt = useRef(0);
  const lastSwingAt = useRef(0);
  const comboRef = useRef(0);
  const comboExpireAt = useRef(0);
  const freeSwingArmed = useRef(false);
  // FX-1 refs — the cast hot path (game logic reads these; state
  // above is for renderers/HUD, same split as pos/posRef).
  const chaoRef = useRef(block?.chao ?? 0);
  const animaRef = useRef(block?.anima ?? 1);
  const castSeq = useRef(0);
  const cooldownAt = useRef({}); //   slotId → readyAt (ms)
  const pawsUntil = useRef(0); //     Spectral Paws expiry
  const ghostUntil = useRef(0); //    Ghost Form expiry
  const ghostRef = useRef(false); //  hot-path read (movement/swing)
  const nineUsed = useRef(false); //  Nine Lives spent (ref mirror)
  const [, setCdTick] = useState(0); // coarse HUD tick while timers live

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);
  useEffect(() => {
    lytRef.current = lyt;
  }, [lyt]);

  // Stat block changes (level-up mid-session) top the pools up.
  useEffect(() => {
    if (!block) return;
    setAnima((a) => Math.min(block.anima, Math.max(a, 1)));
    setLyt((l) => Math.min(block.lytCap, l));
    setChao((c) => Math.min(block.chao ?? 0, c));
  }, [block]);
  useEffect(() => { animaRef.current = anima; }, [anima]);
  useEffect(() => { chaoRef.current = chao; }, [chao]);

  // ---- GATE-1: slot gates — the AENERGY GATE, not the Ascent --
  // The tier is still real (bodies change at L30/L60) but it no
  // longer decides anything. A slot lights when you can HOLD its
  // requirement — checked against the MAX pool, which is Gate A
  // wearing its field clothes. SkillBar derives identically.
  const tier = tierForLevel(level);
  const unlockMap = SLOT_UNLOCK_NODES[character?.beingId] || null;
  const line =
    (being?.lines || []).find((l) => l.id === character?.equippedLineId) ||
    (being?.lines || [])[0] || null;

  // MAX pool — what this character can ever hold at this level.
  const maxPool = useMemo(() => aenergyPool(block, level), [block, level]);

  const canHold = useCallback(
    (slotId) => !aenergyShortfall(maxPool, CAST[slotId]?.aenergyReq),
    [maxPool]
  );

  const slotUnlocked = useCallback(
    (slotId) => {
      if (DEV_PREVIEW_ALL_SLOTS) return true;
      if (slotId === "primera") return Boolean(line?.primera) && canHold("primera");
      if (slotId === "impera") return Boolean(line?.impera) && canHold("impera");
      if (slotId === "uhlti") return Boolean(line?.uhlti) && canHold("uhlti");
      if (slotId === "transformation")
        return Boolean(unlockMap && spent[unlockMap.transformation] && being?.transformation);
      if (slotId === "kings_grace")
        return Boolean(unlockMap && spent[unlockMap.kings_grace]);
      return false;
    },
    [line, unlockMap, spent, being, canHold]
  );

  // King's Grace — THE aura (worn while armed). Nine Lives arms
  // when the slot is unlocked and unspent; the trigger lives in
  // the fatal-damage intercept below. Once per village visit —
  // per-mount today, the §4.13 accepted gap, flagged §4.26.
  const graceArmed = slotUnlocked("kings_grace") && !nineSpent;
  const graceRef = useRef(false);
  graceRef.current = graceArmed;

  const isInvulnerable = useCallback(() => Date.now() < invulnUntil.current, []);

  // ---- v4 FREE-MOVE: mouse/stick aim ------------------------
  // When an aim vector is set (mouse-look writes it), facing
  // follows the aim continuously via facingRef — full 360°.
  // Movement keys stop steering facing while an aim is live.
  // null clears it (facing falls back to movement direction).
  const aimRef = useRef(null);
  const setAim = useCallback((v) => {
    if (!v) { aimRef.current = null; return; }
    const len = Math.hypot(v.x, v.y) || 1;
    const n = { x: v.x / len, y: v.y / len };
    aimRef.current = n;
    facingRef.current = n; // hot path — refs only, no re-render
  }, []);

  // coarse facing state for HUD/2D consumers — octant-gated so
  // continuous mouse aim never storms React.
  const octRef = useRef(-1);
  const commitFacingState = useCallback((f) => {
    const oct = Math.round(Math.atan2(f.y, f.x) / (Math.PI / 4)) & 7;
    if (oct !== octRef.current) {
      octRef.current = oct;
      setFacing({ x: f.x, y: f.y });
    }
  }, []);

  // ---- movement tick (v4: analog integrator) ----------------
  // Position is continuous in tile space. Diagonals normalized,
  // speed identical to the old grid feel. Blocked moves slide
  // along the free axis instead of dead-stopping on a tree.
  useEffect(() => {
    let last = Date.now();
    const t = setInterval(() => {
      const now = Date.now();
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (dodging || now < swingLockUntil.current) return;
      let dx = 0;
      let dy = 0;
      for (const [key, dir] of Object.entries(DIRS)) {
        if (keysRef.current[key]) {
          dx += dir.x;
          dy += dir.y;
        }
      }
      if (!dx && !dy) return;
      const len = Math.hypot(dx, dy);
      dx /= len; dy /= len;
      // facing: movement steers it only when no aim is live
      if (!aimRef.current) {
        facingRef.current = { x: dx, y: dy };
      }
      commitFacingState(facingRef.current);
      const cur = posRef.current;
      // FX-1: Ghost Form — "movement speed doubles" (hot path, ref)
      const step = FEEL.moveSpeedTiles * dt * (ghostRef.current ? CAST.transformation.speedMult : 1);
      const clamp = (p) => ({
        x: Math.max(bounds.minX, Math.min(bounds.maxX, p.x)),
        y: Math.max(bounds.minY, Math.min(bounds.maxY, p.y)),
      });
      let next = clamp({ x: cur.x + dx * step, y: cur.y + dy * step });
      if (isBlocked && isBlocked(next)) {
        // slide: try each axis alone (the AI's own slide law)
        const slideX = clamp({ x: cur.x + dx * step, y: cur.y });
        const slideY = clamp({ x: cur.x, y: cur.y + dy * step });
        next = (!isBlocked(slideX) && dx) ? slideX
          : (!isBlocked(slideY) && dy) ? slideY
          : cur;
      }
      if (next.x === cur.x && next.y === cur.y) return;
      posRef.current = next;
      setPos(next);
    }, FEEL.moveTickMs);
    return () => clearInterval(t);
  }, [bounds, dodging, isBlocked, commitFacingState]);

  // ---- Lyt regen — full pool in ~2.5s of not attacking ------
  useEffect(() => {
    if (!block) return;
    const perTick = block.lytCap / ((LYT_FULL_REGEN_SECONDS * 1000) / 100);
    const t = setInterval(() => {
      if (Date.now() - lastSwingAt.current < FEEL.lytRegenDelayMs) return;
      setLyt((l) => Math.min(block.lytCap, l + perTick));
    }, 100);
    return () => clearInterval(t);
  }, [block]);

  // ---- FX-1: Chao regen + cast housekeeping (one 250ms tick) --
  // Spec law: passive regen tied to tier (2/3/4 per sec). Same
  // interval expires the Spectral Paws window and Ghost Form, and
  // bumps a coarse state tick while any cooldown is live so HUD
  // cooldown readouts stay honest even when nothing else renders.
  useEffect(() => {
    if (!block) return;
    const perSec = CHAO_REGEN_PER_SEC[tier] || CHAO_REGEN_PER_SEC.beb;
    const t = setInterval(() => {
      const now = Date.now();
      const cap = block.chao ?? 0;
      if (chaoRef.current < cap) {
        const next = Math.min(cap, chaoRef.current + perSec * 0.25);
        chaoRef.current = next;
        setChao(next);
      }
      if (pawsUntil.current && now > pawsUntil.current) {
        pawsUntil.current = 0;
        setPawsArmed(false);
      }
      if (ghostUntil.current && now > ghostUntil.current) {
        ghostUntil.current = 0;
        ghostRef.current = false;
        setGhostForm(false);
        onEvent?.({ type: "cast_end", abilityId: being?.transformation?.id || "transformation" });
      }
      if (Object.values(cooldownAt.current).some((at) => at > now)) {
        setCdTick((n) => n + 1);
      }
    }, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, tier]);

  // ---- GATE B — the live Aenergy pool ------------------------
  // Reads the HOT REFS, never React state: this is called inside
  // the cast path and must be honest to the millisecond. Lyf is
  // a threshold you hold, never a currency you spend, so it
  // reports the block value straight through.
  const livePool = useCallback(
    () => ({
      anima: animaRef.current,
      chao: chaoRef.current,
      lyf: block?.lyf ?? 0,
      lyt: lytRef.current,
    }),
    [block]
  );

  /**
   * Can this cost be paid out of the live pool right now?
   * Anima can never be spent to zero — a U'hlti is a sacrifice,
   * not a suicide (it floors at 1).
   */
  const canPayCost = useCallback(
    (cost) => {
      if (!cost) return null;
      const pool = livePool();
      for (const stat of SPENDABLE_AENERGY) {
        const need = cost[stat];
        if (need == null) continue;
        const floor = stat === "anima" ? 1 : 0;
        if ((pool[stat] ?? 0) - need < floor) {
          return { stat, label: AENERGY_LABEL[stat] || stat, need, have: pool[stat] ?? 0 };
        }
      }
      return null;
    },
    [livePool]
  );

  /** Deduct a cost from the live pools. Refs first, then state. */
  const payCost = useCallback((cost) => {
    if (!cost) return;
    if (cost.chao != null) {
      const next = Math.max(0, chaoRef.current - cost.chao);
      chaoRef.current = next;
      setChao(next);
    }
    if (cost.anima != null) {
      const next = Math.max(1, animaRef.current - cost.anima);
      animaRef.current = next;
      setAnima(next);
    }
    if (cost.lyt != null) {
      const next = Math.max(0, lytRef.current - cost.lyt);
      lytRef.current = next;
      setLyt(next);
    }
  }, []);

  // ---- DECREE — the earned opening ---------------------------
  // Which triggers does this hit satisfy? Two read live state
  // that already exists (combo end, your own Primera window);
  // two read OPTIONAL fields on the target, so a zone opts in by
  // exposing `facing` or `staggered`/`stunUntil`/`marked` and
  // nothing breaks until it does. No trigger is ever random.
  const decreeFor = useCallback((target, { origin, comboEnd, primeraWindow }) => {
    const out = [];
    const tf = target?.facing;
    if (tf && (tf.x || tf.y)) {
      const away = { x: origin.x - (target.pos?.x ?? 0), y: origin.y - (target.pos?.y ?? 0) };
      const len = Math.hypot(away.x, away.y) || 1;
      // the target is looking away from where the blow came from
      if ((tf.x * away.x + tf.y * away.y) / len < -0.35) out.push(DECREE_TRIGGERS.behind);
    }
    const now = Date.now();
    const setUp =
      target?.staggered ||
      target?.stunned ||
      (target?.stunUntil || 0) > now ||
      target?.marked ||
      Boolean(target?.marks?.vessel_mark);
    if (setUp) out.push(DECREE_TRIGGERS.marked);
    if (comboEnd) out.push(DECREE_TRIGGERS.comboEnd);
    if (primeraWindow) out.push(DECREE_TRIGGERS.primera);
    return out;
  }, []);

  // ---- THE LYT ATTACK — the combo --------------------------
  const attack = useCallback(() => {
    const now = Date.now();
    if (dodging || now < swingLockUntil.current) return;

    // combo bookkeeping
    if (now > comboExpireAt.current) comboRef.current = 0;
    const step = comboRef.current + 1; // 1-indexed swing number

    // pay for the swing (Phase Momentum: post-dodge swing is free)
    let free = false;
    if (mods.freeSwingAfterDodge && freeSwingArmed.current) {
      free = true;
      freeSwingArmed.current = false;
    }
    if (!free) {
      const paid = payLytSwing(Math.floor(lytRef.current));
      if (!paid.ok) {
        // the ~2-second breather (origin lets zones show the pant)
        onEvent?.({ type: "dry", origin: { ...posRef.current } });
        return;
      }
      setLyt((l) => Math.max(0, l - 1));
    }
    lastSwingAt.current = now;
    swingLockUntil.current = now + FEEL.swingRecoverMs;
    comboRef.current = step >= comboLength ? 0 : step;
    comboExpireAt.current = now + FEEL.comboWindowMs;
    setComboStep(step);

    // iframes: the phase-swipe follow-through (swing 2 for the
    // Tiger), the whole combo with Ghost-Walker
    const phaseSwing = step === 2 || mods.iframesWholeCombo;
    if (phaseSwing) {
      invulnUntil.current = Math.max(
        invulnUntil.current,
        now + FEEL.swingIframeMs * mods.iframeMultiplier
      );
    }

    // resolve hits — everything in the front arc
    const reach = FEEL.attackArcTiles;
    const origin = posRef.current;
    const f = facingRef.current;
    const hitPoint = { x: origin.x + f.x * 1, y: origin.y + f.y * 1 };
    const all = getTargets ? getTargets() : [];
    const targets = all.filter(
      (t) => t.alive !== false && dist(t.pos, hitPoint) <= reach
    );

    // FX-1: Spectral Paws — "next Lyt attack ignores enemy
    // defenses and targets the lowest-Anima enemy in range."
    // Consumed by this swing: Lyf ignored on every hit, and the
    // lowest-Anima enemy within extended reach is sought out even
    // if it sits outside the normal arc. The hunt finds the weak.
    const pawsLive = pawsUntil.current > now;
    if (pawsLive) {
      pawsUntil.current = 0;
      setPawsArmed(false);
      const seekReach = reach * 1.6;
      const weakest = all
        .filter((t) => t.alive !== false && dist(t.pos, origin) <= seekReach)
        .sort((a, b) => (a.anima ?? Infinity) - (b.anima ?? Infinity))[0];
      if (weakest && !targets.some((t) => t.id === weakest.id)) targets.push(weakest);
    }

    const skillBonus = step === 1 ? mods.firstSwingBonus : 0;
    const finalHit = step === comboLength;
    for (const t of targets) {
      const bleeding = (t.bleedStacks || 0) > 0;
      // THE DECREE: you didn't drop the string, you came in from
      // behind, they were already staggered, you were inside your
      // own Primera window. Every one of those you did on purpose.
      const triggers = decreeFor(t, {
        origin,
        comboEnd: finalHit,
        primeraWindow: pawsLive,
      });
      const { damage, decree } = resolveDamage({
        attack: block?.attack ?? 1,
        skillBonus,
        targetLyf: pawsLive ? 0 : t.lyf ?? 0, // Paws: defenses ignored
        decreeTriggers: triggers,
        // Third Strike makes the end of a full string hit harder;
        // it does NOT make it likelier, because there is no
        // likelihood any more.
        decreeBonus: finalHit ? mods.comboEndDecreeBonus : 0,
      });
      // Heart-Seeker: bleeding targets take amplified Lyt damage
      let dealt = bleeding ? Math.round(damage * mods.bleedAmp) : damage;
      // Ghost Form: "phantom swipes — lower damage, cannot miss"
      if (ghostRef.current) dealt = Math.max(1, Math.round(dealt * CAST.transformation.dmgMult));
      onHit?.({
        targetId: t.id,
        damage: dealt,
        decree,
        decreeTriggers: triggers,
        crit: decree, //  compat alias — zone hit-FX flare on this
        bleedApplied: mods.applyBleed,
        swing: step,
      });
    }
    // the body strikes — one record per swing, keyed for re-trigger
    setLastSwing({ id: ++swingSeq.current, step, at: now });
    onEvent?.({
      type: "swing",
      step,
      connected: targets.length,
      free,
      // visual contract (July 19 FX pass): where it happened,
      // which way it went, and WHICH move it was (vyren id —
      // AbilityFX keys on this directly).
      origin: { ...origin },
      facing: { ...f },
      hitPoint,
      lytId: being?.lytAttack?.id || null,
      paws: pawsLive, // FX-1 — the empowered strike reads different
    });
  }, [dodging, mods, comboLength, block, getTargets, onHit, onEvent, being, decreeFor]);

  // ---- FX-1: THE CAST — slots 2–5 as live verbs ---------------
  // One verb, gated exactly like SkillBar lights its slots. Pays
  // Chao, arms cooldowns, resolves damage ONLY through combat.js,
  // reports hits through onHit (zones keep owning enemy state),
  // and emits one lastCast record — the 3D posture + world FX key
  // on it the same way lastSwing drives the strike.
  const cast = useCallback(
    (slotId) => {
      const now = Date.now();
      if (dodging || now < swingLockUntil.current) return;
      const def = CAST[slotId];
      if (!def) return;
      if (!slotUnlocked(slotId)) {
        onEvent?.({ type: "cast_locked", slotId });
        return;
      }
      if (now < (cooldownAt.current[slotId] || 0)) {
        onEvent?.({ type: "cast_cooling", slotId, readyAt: cooldownAt.current[slotId] });
        return;
      }
      if (slotId === "transformation" && ghostRef.current) return; // already worn

      // ── GATE B — ACTIVATION (checks the CURRENT pool) ────────
      // Same field, same helper as the Trainer's Gate A; a
      // different number. A Lord with 200 max Chao and 14 left
      // cannot fire their U'hlti. They get to swinging.
      const short = aenergyShortfall(livePool(), def.aenergyReq);
      if (short) {
        onEvent?.({
          type: "aenergy_low",
          slotId,
          ...short,
          origin: { ...posRef.current },
        });
        return;
      }
      const unpayable = canPayCost(def.cost);
      if (unpayable) {
        onEvent?.({
          type: "aenergy_dry",
          slotId,
          ...unpayable,
          origin: { ...posRef.current },
          // compat alias — HUDs listening for the old event still
          // hear it when the shortfall is Chao, which is all of
          // them today. Drop when the zones move to aenergy_dry.
          legacy: unpayable.stat === "chao" ? "chao_dry" : null,
        });
        if (unpayable.stat === "chao") {
          onEvent?.({ type: "chao_dry", slotId, origin: { ...posRef.current } });
        }
        return;
      }

      // resolve the ability id from the being registry (vyren —
      // the id IS the FX key, in 2D and 3D both)
      let abilityId = null;
      if (slotId === "primera") abilityId = line?.primera?.id;
      else if (slotId === "impera") abilityId = line?.impera?.id;
      else if (slotId === "uhlti") abilityId = line?.uhlti?.id;
      else if (slotId === "transformation") abilityId = being?.transformation?.id;
      if (!abilityId) return;

      // pay + arm — the cost may span several Aenergy (Fluidity
      // Law); payCost deducts each one it names and no others.
      payCost(def.cost);
      cooldownAt.current[slotId] = now + def.cdMs;
      swingLockUntil.current = now + FEEL.swingRecoverMs; // casts share the swing beat

      const origin = { ...posRef.current };
      const f = { ...facingRef.current };
      const all = (getTargets ? getTargets() : []).filter((t) => t.alive !== false);
      let targets = [];

      if (slotId === "primera") {
        // Spectral Paws — arm the next Lyt (consumed in attack())
        pawsUntil.current = now + def.armedMs;
        setPawsArmed(true);
      } else if (slotId === "impera") {
        // Spectral Claws — three phantom claws seek the three
        // lowest-Anima enemies; bonus proportional to missing Anima
        targets = all
          .filter((t) => dist(t.pos, origin) <= def.rangeTiles)
          .sort((a, b) => (a.anima ?? Infinity) - (b.anima ?? Infinity))
          .slice(0, def.claws)
          .map((t) => ({ id: t.id, pos: { ...t.pos } }));
        for (const tp of targets) {
          const t = all.find((e) => e.id === tp.id);
          if (!t) continue;
          // taxonomy enemies carry animaMax (buildEnemy); a target
          // without it simply contributes no missing-Anima bonus
          const missing = t.animaMax != null ? Math.max(0, t.animaMax - (t.anima ?? 0)) : 0;
          const triggers = decreeFor(t, { origin, comboEnd: false, primeraWindow: pawsUntil.current > now });
          const { damage, decree } = resolveDamage({
            attack: block?.attack ?? 1,
            skillBonus: def.bonusPerClaw + Math.round(missing * def.missingAnimaPct),
            targetLyf: t.lyf ?? 0,
            decreeTriggers: triggers,
          });
          onHit?.({
            targetId: t.id, damage, decree, decreeTriggers: triggers,
            crit: decree, bleedApplied: false, source: abilityId,
          });
        }
      } else if (slotId === "uhlti") {
        // Haunting Presence — HER move. All enemies on screen take
        // simultaneous shadow damage; all elites bleed for 5s.
        targets = all.map((t) => ({ id: t.id, pos: { ...t.pos } }));
        for (const t of all) {
          const elite = ["elite", "sub_boss", "subboss", "world_boss"].includes(t.role);
          const { damage } = resolveDamage({
            attack: block?.attack ?? 1,
            skillBonus: def.bonus,
            targetLyf: def.bypassLyf ? 0 : t.lyf ?? 0,
          });
          onHit?.({
            targetId: t.id, damage, decree: false, decreeTriggers: [],
            crit: false, bleedApplied: elite, source: abilityId,
          });
        }
      } else if (slotId === "transformation") {
        // Ghost Form — fully incorporeal for 8s (holy still bites)
        ghostUntil.current = now + def.durationMs;
        ghostRef.current = true;
        setGhostForm(true);
      }

      const rec = { id: ++castSeq.current, abilityId, slotId, at: now, origin, facing: f, targets };
      setLastCast(rec);
      onEvent?.({ type: "cast", ...rec });
    },
    [dodging, slotUnlocked, line, being, block, getTargets, onHit, onEvent,
     livePool, canPayCost, payCost, decreeFor]
  );

  // ---- THE DODGE — dash + iframes ---------------------------
  const dodge = useCallback(() => {
    const now = Date.now();
    if (dodging || now < dodgeReadyAt.current) return;
    setDodging(true);
    dodgeReadyAt.current = now + FEEL.dodgeCooldownMs;
    invulnUntil.current = Math.max(invulnUntil.current, now + FEEL.dodgeIframeMs);
    if (mods.freeSwingAfterDodge) freeSwingArmed.current = true;

    const f = facingRef.current;
    const cur = posRef.current;
    // v4: continuous dash along the live 360° facing, swept in
    // sub-steps so a tree stops the dash where it should.
    let landing = { ...cur };
    const SUBS = 8;
    for (let i = 1; i <= SUBS; i++) {
      const d = (FEEL.dodgeTiles * i) / SUBS;
      const step = {
        x: Math.max(bounds.minX, Math.min(bounds.maxX, cur.x + f.x * d)),
        y: Math.max(bounds.minY, Math.min(bounds.maxY, cur.y + f.y * d)),
      };
      if (isBlocked && isBlocked(step)) break;
      landing = step;
    }
    posRef.current = landing;
    setPos(landing);
    onEvent?.({ type: "dodge", from: { ...cur }, to: { ...landing }, facing: { ...f } });
    setTimeout(() => setDodging(false), FEEL.dodgeMs);
  }, [dodging, bounds, isBlocked, mods, onEvent]);

  // ---- FX-1: Nine Lives — the King's Grace trigger ------------
  // "On fatal damage, the Tiger dissolves and reforms at the
  // nearest shadow with 50% Anima." One intercept, called by both
  // damage entry points. Reform blinks away from the killing blow
  // (opposite the live facing, first unblocked spot), with a
  // mercy-iframe window so the reform isn't a second death.
  const tryNineLives = useCallback(
    (wouldBeAnima) => {
      if (wouldBeAnima > 0) return null;
      if (!graceRef.current || nineUsed.current) return null;
      nineUsed.current = true;
      setNineSpent(true);
      const now = Date.now();
      const revived = Math.max(1, Math.round((block?.anima ?? 1) * 0.5));
      const f = facingRef.current;
      const cur = posRef.current;
      const clamp = (p) => ({
        x: Math.max(bounds.minX, Math.min(bounds.maxX, p.x)),
        y: Math.max(bounds.minY, Math.min(bounds.maxY, p.y)),
      });
      let landing = { ...cur };
      for (let d = 2.5; d >= 0.5; d -= 0.5) {
        const p = clamp({ x: cur.x - (f.x || 0) * d, y: cur.y - (f.y || 1) * d });
        if (!isBlocked || !isBlocked(p)) { landing = p; break; }
      }
      const rec = {
        id: ++castSeq.current,
        abilityId: "nine_lives",
        slotId: "kings_grace",
        at: now,
        origin: { ...cur },
        facing: { ...f },
        targets: [],
        reformAt: { ...landing },
      };
      posRef.current = landing;
      setPos(landing);
      animaRef.current = revived;
      setAnima(revived);
      invulnUntil.current = Math.max(invulnUntil.current, now + 1200);
      setLastCast(rec);
      onEvent?.({ type: "cast", ...rec });
      return revived;
    },
    [block, bounds, isBlocked, onEvent]
  );

  // ---- incoming damage (zones call this) --------------------
  const takeHit = useCallback(
    ({ attack: enemyAttack, skillBonus = 0, holy = false }) => {
      if (isInvulnerable()) {
        onEvent?.({ type: "hurt", dodged: true, damage: 0 });
        return { damage: 0, dodged: true };
      }
      // FX-1: Ghost Form — "most enemy attacks pass through."
      // The counterplay door: hits flagged { holy: true } (holy-
      // affinity enemies, Anti-Ghost elites, Holy-ilk Tomes)
      // still strike the Tiger. Light is always the weakness.
      if (ghostRef.current && !holy) {
        onEvent?.({ type: "hurt", dodged: true, damage: 0, phased: true });
        return { damage: 0, dodged: true };
      }
      // Enemies never Decree. Decree is the PLAYER's earned
      // opening; a mook that wanders behind you did not earn it.
      const { damage } = resolveDamage({
        attack: enemyAttack,
        skillBonus,
        targetLyf: block?.lyf ?? 0,
      });
      const next = Math.max(0, animaRef.current - damage);
      if (tryNineLives(next) != null) {
        onEvent?.({ type: "hurt", dodged: false, damage, nineLives: true });
        return { damage, dodged: false, nineLives: true };
      }
      animaRef.current = next;
      setAnima(next);
      onEvent?.({ type: "hurt", dodged: false, damage });
      return { damage, dodged: false };
    },
    [block, isInvulnerable, onEvent, tryNineLives]
  );

  // DoT ticks bypass Lyf — one entry point, spec law.
  const takeDotTick = useCallback(
    (perSecond) => {
      const dmg = resolveDotTick(perSecond);
      const next = Math.max(0, animaRef.current - dmg);
      if (tryNineLives(next) != null) return dmg;
      animaRef.current = next;
      setAnima(next);
      return dmg;
    },
    [tryNineLives]
  );

  // ---- keyboard --------------------------------------------
  useEffect(() => {
    const down = (e) => {
      if (DIRS[e.key]) {
        keysRef.current[e.key] = true;
        e.preventDefault();
      } else if (e.key === " " || e.key === "j" || e.key === "J") {
        e.preventDefault();
        // v4 FIX: OS key auto-repeat was re-firing attack() every
        // ~30ms while held, each re-arming the 220ms swing lock —
        // chaining into "my movement doesn't register after I
        // attack." One press, one swing.
        if (e.repeat) return;
        attack();
      } else if (e.key === "Shift" || e.key === "k" || e.key === "K") {
        e.preventDefault();
        if (e.repeat) return;
        dodge();
      } else if (e.key === "2" || e.key === "3" || e.key === "4" || e.key === "5") {
        // FX-1: the cast keys — digits mirror the skill-bar slots.
        // EVERY discrete-action key guards e.repeat (the law).
        e.preventDefault();
        if (e.repeat) return;
        const slot = { 2: "primera", 3: "impera", 4: "uhlti", 5: "transformation" }[e.key];
        cast(slot);
      }
    };
    const up = (e) => {
      if (DIRS[e.key]) keysRef.current[e.key] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [attack, dodge, cast]);

  return {
    // render state
    pos,
    facing,
    dodging,
    comboStep,
    lastSwing, // pass to AnnihilationPlayer — the strike posture
    anima,
    animaMax: block?.anima ?? 1,
    lyt,
    lytMax: block?.lytCap ?? 1,
    chao, //                   FX-1 — the cast pool
    chaoMax: block?.chao ?? 0,
    level,
    being,
    mods,
    comboLength,
    // FX-1 cast surface — zones pass these straight to the 3D
    // renderer (lastCast drives postures + world FX the same way
    // lastSwing drives the strike)
    lastCast,
    pawsArmed, //              Spectral Paws buff live (paw-fire visual)
    ghostForm, //              Ghost Form live (incorporeal treatment)
    graceArmed, //             King's Grace worn — THE aura
    slotUnlocked, //           SkillBar-identical gate derivation
    castReady: (slotId) => Date.now() >= (cooldownAt.current[slotId] || 0),
    cooldownLeft: (slotId) => Math.max(0, (cooldownAt.current[slotId] || 0) - Date.now()),
    // GATE-1 surface — HUDs and SkillBar read these to print WHY
    // a slot is dark ("200 Chao") instead of a tier name.
    maxPool, //                what this character can ever hold
    livePool, //               () => the pool right now
    canHold, //                (slotId) => can you carry it at all
    castRequirement: (slotId) => describeAenergyReq(CAST[slotId]?.aenergyReq),
    castCost: (slotId) => CAST[slotId]?.cost || null,
    // verbs (mobile pads + zones)
    attack,
    dodge,
    cast, //                   FX-1 — cast(slotId): primera/impera/uhlti/transformation
    takeHit,
    takeDotTick,
    isInvulnerable,
    keysRef, //   feed MobileDpad
    setAim, //    v4 — mouse-look/stick writes a 360° aim vector (null clears)
    // PERF (v3): live refs — 3D renderers read these in useFrame
    // so movement never has to reconcile the React tree. pos/facing
    // state above remain the source of truth for game logic.
    posRef,
    facingRef,
    setAnima, // village rest / potions (owned by zones, law-checked there)
  };
}

// ------------------------------------------------------------
// BLEED helper for zones — the standard stacking DoT ledger.
// Zones own their enemy state; this keeps the bleed law (3/sec,
// 5 stacks max, 5s refresh) in one place instead of per-zone.
// ------------------------------------------------------------
export const BLEED = STATUS_EFFECTS.bleed;
export const MAX_BLEED_STACKS = 5;

export const applyBleedStack = (enemy) => ({
  ...enemy,
  bleedStacks: Math.min(MAX_BLEED_STACKS, (enemy.bleedStacks || 0) + 1),
  bleedExpiresAt: Date.now() + BLEED.durationSec * 1000,
});

export const tickBleed = (enemy) => {
  // PERF (v3): identity law — if there's nothing to tick, hand the
  // SAME object back. New objects here defeated React.memo on every
  // enemy every second even when no one was bleeding.
  if (!enemy.bleedStacks) return { enemy, damage: 0 };
  if (Date.now() > (enemy.bleedExpiresAt || 0)) {
    return { enemy: { ...enemy, bleedStacks: 0 }, damage: 0 };
  }
  const damage = resolveDotTick(BLEED.dps * enemy.bleedStacks);
  return {
    enemy: { ...enemy, anima: Math.max(0, (enemy.anima || 0) - damage) },
    damage,
  };
};
